// BUILD-046 increment 1b-i: how people are DRAWN as initials circles. What a circle says (its letters, colour and the name behind it) is personAvatarModel.ts's; when a popover is open, closed or
// replaced is avatarPinModel.ts's; this file draws them.
//
//   AvatarCircle   a plain decorative circle (aria-hidden, not focusable). Used where the name is written beside it (the recipient list of an Everyone request, the people search lists).
//   PersonAvatar   one person as a real <button type="button">, so Tab reaches it and Enter / Space operate it. Its accessible name is the FULL name (and the department when known); the letters are
//                  aria-hidden. The full name is on
//                    HOVER and KEYBOARD FOCUS  the existing name tooltip (PersonName.useNameTip: role=tooltip, aria-describedby while shown, Escape dismisses, same look as the other names);
//                    TOUCH / CLICK / Enter / Space  a PINNED popover with the same words, toggled by each activation and closed by a second one, a press outside, Escape or blur.
//                  After a touch press the replayed mouse-enter and the focus a tap brings are ignored (avatarPinModel.acceptsHover / acceptsFocus), so the second tap can close the popover. A tap
//                  never scrolls the table (nothing is focused or scrolled into view) and never reaches a row action (the click stops here).
//   AvatarGroup    up to three circles in the cell, then a "+N" button (aria-expanded, a real button) that opens a popover listing the REST with a circle each, in full. Escape closes it and gives focus back
//                  to the button when focus was inside the popover. The labels are unique across the whole group (personAvatarModel.describeAvatarGroup), and the same person listed twice is one circle.
//
// A narrow cell never repeats a name beside a circle: the name is the circle's accessible name, tooltip and popover, nothing else. TAB STOPS: one per circle and one for "+N".
//
// The popover and the tooltip are drawn through a portal into <body> with fixed coordinates (like PersonName.tsx and RequesterBadge.tsx), so a table's overflow-hidden can never clip them.
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, FocusEvent as ReactFocusEvent, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, ReactNode, MutableRefObject } from 'react';
import { createPortal } from 'react-dom';
import { INITIAL_PIN_STATE, acceptsFocus, acceptsHover, overlayOf, placeBubble, reducePin } from './avatarPinModel';
import type { BubblePlace, PinEvent, PinState } from './avatarPinModel';
import { AVATAR_INLINE_MAX, describeAvatarGroup } from './personAvatarModel';
import type { AvatarBadge, AvatarPersonInput, AvatarSize } from './personAvatarModel';
import { useNameTip } from './PersonName';

// Whole class strings (not built from pieces) so the style scanner sees them. A circle is a pill when its label is three characters ("DAN", "SL1").
const CIRCLE_CLASS: Readonly<Record<AvatarSize, string>> = Object.freeze({
  comfortable: 'inline-flex h-7 min-w-7 shrink-0 select-none items-center justify-center rounded-full px-1 text-[11px] font-semibold leading-none tracking-normal',
  compact: 'inline-flex h-[22px] min-w-[22px] shrink-0 select-none items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none tracking-normal',
});

// The button grows its tap target by 4 px on every side without growing the circle (WCAG 2.5.8: at least 24 CSS px, 30 and 36 here).
const BUTTON_CLASS = "relative touch-manipulation after:absolute after:-inset-1 after:content-[''] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8] focus-visible:ring-offset-1";

const MORE_CLASS: Readonly<Record<AvatarSize, string>> = Object.freeze({
  comfortable: 'inline-flex h-7 min-w-7 shrink-0 select-none items-center justify-center rounded-full border border-gray-300 bg-gray-100 px-1.5 text-[11px] font-semibold leading-none text-gray-800 hover:bg-gray-200',
  compact: 'inline-flex h-[22px] min-w-[22px] shrink-0 select-none items-center justify-center rounded-full border border-gray-300 bg-gray-100 px-1 text-[10px] font-semibold leading-none text-gray-800 hover:bg-gray-200',
});

/** The look of the other name tooltips (PersonName.useNameTip), so a pinned popover and a hover tooltip are the same thing to the eye. */
const BUBBLE_CLASS = 'rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-normal normal-case leading-snug tracking-normal text-white shadow-lg';

function colorStyle(badge: AvatarBadge): CSSProperties {
  return { backgroundColor: badge.color.background, color: badge.color.text };
}

/** A decorative circle: the label on the person's colour. Not focusable and hidden from assistive technology (the name is written beside it). */
export function AvatarCircle({ badge, size = 'comfortable' }: { badge: AvatarBadge; size?: AvatarSize }) {
  return (
    <span aria-hidden="true" data-testid="avatar-circle" data-avatar-label={badge.label} className={CIRCLE_CLASS[size]} style={colorStyle(badge)}>
      {badge.label}
    </span>
  );
}

// ───────────────────────── the hover / focus / pin wiring of one button ─────────────────────────

/** useNameTip's handlers do not read their event; this is passed where there is no React event to hand (a native Escape, a pointer press that resets the tooltip). */
const NO_EVENT = undefined as never;

/** True when the focus the browser just gave the element is KEYBOARD focus (:focus-visible). A browser without the selector counts as keyboard, which keeps the tooltip (the old behaviour). */
function isKeyboardFocus(element: Element): boolean {
  try {
    return element.matches(':focus-visible');
  } catch {
    return true;
  }
}

interface PinWiring {
  state: PinState;
  tip: ReturnType<typeof useNameTip>;
  popoverId: string;
  popoverRef: MutableRefObject<HTMLDivElement | null>;
  anchorRef: MutableRefObject<HTMLButtonElement | null>;
  /** Props for the button. */
  buttonProps: {
    ref: (element: HTMLButtonElement | null) => void;
    onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void;
    onMouseEnter: (event: ReactMouseEvent<HTMLButtonElement>) => void;
    onMouseLeave: (event: ReactMouseEvent<HTMLButtonElement>) => void;
    onFocus: (event: ReactFocusEvent<HTMLButtonElement>) => void;
    onBlur: (event: ReactFocusEvent<HTMLButtonElement>) => void;
    onKeyDown: () => void;
    onClick: (event: ReactMouseEvent<HTMLButtonElement>) => void;
  };
}

/**
 * The wiring shared by a person's circle and the "+N" button: the hover / focus tooltip (useNameTip) and the pin (avatarPinModel). `tipText` is what the tooltip says.
 * `tooltipYields` is true where the pinned popover SAYS SOMETHING ELSE than the tooltip (the "+N" list): the tooltip is then withdrawn while the popover is pinned (so the two never overlap).
 * For a person's circle the two say the same words, so the tooltip is simply left on while it is open. Escape clears the hover and focus that would bring a tooltip straight back.
 */
function usePinWiring(tipText: string | null, tooltipYields: boolean): PinWiring {
  const [state, setState] = useState<PinState>(INITIAL_PIN_STATE);
  const latest = useRef<PinState>(INITIAL_PIN_STATE);
  const tip = useNameTip(state.pinned && tooltipYields ? null : tipText);
  const popoverId = useId();
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  function send(event: PinEvent): void {
    const next = reducePin(latest.current, event);
    if (next === latest.current) return;
    latest.current = next;
    setState(next);
  }

  // While pinned: Escape and a press outside the circle and its popover close it.
  useEffect(() => {
    if (!state.pinned) return undefined;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      const focusWasInside = popoverRef.current !== null && popoverRef.current.contains(document.activeElement);
      send({ type: 'escape' });
      // Focus goes back to the circle only when it was inside the popover; a circle that never had focus (a touch pin) is not made the focus owner by Escape.
      if (focusWasInside) anchorRef.current?.focus({ preventScroll: true });
      // Escape dismisses everything: the hover and focus that would bring the tooltip straight back are cleared (they come back on the next enter or Tab).
      tip.handlers.onMouseLeave(NO_EVENT);
      tip.handlers.onBlur(NO_EVENT);
    }
    function onPointerDown(event: PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if ((anchorRef.current !== null && anchorRef.current.contains(target)) || (popoverRef.current !== null && popoverRef.current.contains(target))) return;
      send({ type: 'outside-press' });
    }
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.pinned]);

  const buttonProps: PinWiring['buttonProps'] = {
    ref: (element) => {
      anchorRef.current = element;
      tip.setAnchor(element);
    },
    onPointerDown: (event) => {
      send({ type: 'pointer-down', pointerType: event.pointerType });
      if (event.pointerType === 'touch') {
        // A touch takes over from whatever a mouse left open (a hybrid laptop): the tooltip's own hover / focus are cleared, so only the pin decides what shows.
        tip.handlers.onMouseLeave(NO_EVENT);
        tip.handlers.onBlur(NO_EVENT);
      }
    },
    onMouseEnter: (event) => {
      if (acceptsHover(latest.current)) tip.handlers.onMouseEnter(event);
    },
    onMouseLeave: (event) => tip.handlers.onMouseLeave(event),
    onFocus: (event) => {
      const keyboard = isKeyboardFocus(event.currentTarget);
      if (acceptsFocus(keyboard)) tip.handlers.onFocus(event);
      send({ type: 'focus', keyboard });
    },
    onBlur: (event) => {
      tip.handlers.onBlur(event);
      send({ type: 'blur' });
    },
    onKeyDown: () => send({ type: 'key-down' }),
    onClick: (event) => {
      // The circle is its own control: a tap, click, Enter or Space never reaches the row it sits in.
      event.stopPropagation();
      send({ type: 'activate' });
    },
  };

  return { state, tip, popoverId, popoverRef, anchorRef, buttonProps };
}

/** A fixed-position bubble in <body> under the button (above it near the bottom of the screen), placed by avatarPinModel.placeBubble and kept in place while the page scrolls. */
function Bubble({ anchorRef, bubbleRef, children }: { anchorRef: MutableRefObject<HTMLButtonElement | null>; bubbleRef: MutableRefObject<HTMLDivElement | null>; children: ReactNode }) {
  const [place, setPlace] = useState<BubblePlace | null>(null);
  // Measured before paint, so the bubble never flashes at the wrong place.
  useLayoutEffect(() => {
    function measure(): void {
      const anchor = anchorRef.current;
      const bubble = bubbleRef.current;
      if (anchor === null || bubble === null) return;
      const rect = anchor.getBoundingClientRect();
      setPlace(placeBubble({ top: rect.top, bottom: rect.bottom, left: rect.left }, { width: bubble.offsetWidth, height: bubble.offsetHeight }, { width: window.innerWidth, height: window.innerHeight }));
    }
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const style: CSSProperties = place === null ? { position: 'fixed', top: 0, left: 0, visibility: 'hidden' } : { position: 'fixed', top: place.top, left: place.left };
  return createPortal(
    <div ref={bubbleRef} style={style} className={`z-[60] w-max max-w-sm ${place?.above === true ? 'pb-1' : 'pt-1'}`} onClick={(event) => event.stopPropagation()}>
      {children}
    </div>,
    document.body
  );
}

// ───────────────────────── one person ─────────────────────────

export interface PersonAvatarProps {
  /** From personAvatarModel.avatarLabels / describeAvatarGroup: the label is unique within the group it was made for. */
  badge: AvatarBadge;
  size?: AvatarSize;
}

/** One person as an initials circle: a real button named with the full name (and department), the full name on hover and focus, and a pinned popover on tap / click / Enter / Space. */
export function PersonAvatar({ badge, size = 'comfortable' }: PersonAvatarProps) {
  const wiring = usePinWiring(badge.tipText, false);
  const { state, tip } = wiring;
  // The pinned popover and the tooltip say the same words in the same place: while the tooltip is open (hover, focus) it is the one drawn.
  const chosen = overlayOf(state, tip.open);
  const overlay = chosen === 'pinned' && tip.open ? 'tooltip' : chosen;
  const describedBy = overlay === 'pinned' ? wiring.popoverId : overlay === 'tooltip' ? tip.tipId : undefined;
  return (
    <>
      <button
        type="button"
        {...wiring.buttonProps}
        aria-label={badge.accessibleName}
        aria-describedby={describedBy}
        data-testid="person-avatar"
        data-avatar-label={badge.label}
        className={`${CIRCLE_CLASS[size]} ${BUTTON_CLASS}`}
        style={colorStyle(badge)}
      >
        <span aria-hidden="true">{badge.label}</span>
      </button>
      {overlay === 'tooltip' && tip.overlay}
      {overlay === 'pinned' && (
        <Bubble anchorRef={wiring.anchorRef} bubbleRef={wiring.popoverRef}>
          <div id={wiring.popoverId} role="tooltip" data-testid="person-popover" className={BUBBLE_CLASS}>
            {badge.tipText}
          </div>
        </Bubble>
      )}
    </>
  );
}

// ───────────────────────── a group ─────────────────────────

/** The "+N" button and the popover it opens: every person behind it, a circle and the full name (and department) each. */
function MoreButton({ rest, size }: { rest: AvatarBadge[]; size: AvatarSize }) {
  const names = rest.map((badge) => badge.full).join(', ');
  const wiring = usePinWiring(names, true);
  const { state, tip } = wiring;
  const overlay = overlayOf(state, tip.open);
  const label = `Show ${rest.length} more ${rest.length === 1 ? 'person' : 'people'}: ${names}`;
  return (
    <>
      <button
        type="button"
        {...wiring.buttonProps}
        aria-label={label}
        aria-haspopup="true"
        aria-expanded={state.pinned}
        aria-controls={state.pinned ? wiring.popoverId : undefined}
        aria-describedby={overlay === 'tooltip' ? tip.tipId : undefined}
        data-testid="avatar-more"
        className={`${MORE_CLASS[size]} ${BUTTON_CLASS}`}
      >
        <span aria-hidden="true">+{rest.length}</span>
      </button>
      {overlay === 'tooltip' && tip.overlay}
      {overlay === 'pinned' && (
        <Bubble anchorRef={wiring.anchorRef} bubbleRef={wiring.popoverRef}>
          <div id={wiring.popoverId} role="group" aria-label={`${rest.length} more ${rest.length === 1 ? 'person' : 'people'}`} data-testid="avatar-more-popover" className="min-w-[12rem] max-w-xs rounded-md border border-gray-200 bg-white p-2 text-sm text-gray-900 shadow-lg">
            <ul className="space-y-1.5">
              {rest.map((badge) => (
                <li key={badge.key} className="flex items-center gap-2">
                  <AvatarCircle badge={badge} size="compact" />
                  <span className="min-w-0">
                    <span className="block break-words">{badge.full}</span>
                    {badge.department !== '' && <span className="block text-xs text-gray-600">{badge.department}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Bubble>
      )}
    </>
  );
}

export interface AvatarGroupProps {
  /** The people of one cell. Roster rows pass as they are ({ employeeId, displayName } or { id, name }). The same person twice is one circle. */
  people: readonly (AvatarPersonInput | string)[];
  size?: AvatarSize;
  /** How many circles before "+N" (default 3). */
  max?: number;
  /** Names the group for assistive technology ("Assigned to"); the circles inside are named with the people. */
  label?: string;
  className?: string;
}

/** Up to three circles, then "+N". Nothing is drawn for a group with nobody in it. */
export function AvatarGroup({ people, size = 'comfortable', max = AVATAR_INLINE_MAX, label, className }: AvatarGroupProps) {
  const view = describeAvatarGroup(people, max);
  if (view.people.length === 0) return null;
  return (
    <span role={label !== undefined ? 'group' : undefined} aria-label={label} data-testid="avatar-group" className={`inline-flex min-w-0 items-center gap-1 ${size === 'compact' ? 'flex-nowrap' : 'flex-wrap'} ${className ?? ''}`.trim()}>
      {view.shown.map((badge) => (
        <PersonAvatar key={badge.key} badge={badge} size={size} />
      ))}
      {view.rest.length > 0 && <MoreButton rest={view.rest} size={size} />}
    </span>
  );
}
